import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  signal,
  SecurityContext,
} from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import {
  KcAlertComponent,
  KcButtonComponent,
  KcIconButtonComponent,
  KcSkeletonComponent,
} from '@kaspacom/ui-kit';
import { TokenLogoComponent } from '../token-logo/token-logo.component';
import { Kcc20NotificationsManager } from '../../managers/notifications.manager';
import { Kcc20Notification } from '../../services/notifications/kcc20-notifications-api.service';
import { Kcc20TokenStaticProfileCacheService } from '../../services/tokens/kcc20-token-static-profile-cache.service';
import {
  formatCount,
  formatDateMs,
  notificationCta,
  notificationExplorerHref,
  notificationMessageRows,
  notificationProminence,
  notificationStatusLabel,
  notificationStatusTone,
  notificationTitle,
  notificationTokenId,
  notificationTradeSummary,
  notificationTransactionHash,
  shortIdentifier,
} from '../../shared/kcc20-formatters';

type NotificationFilter = 'all' | 'unread';

@Component({
  selector: 'kcc20-notifications-bell',
  standalone: true,
  imports: [
    TokenLogoComponent,
    KcAlertComponent,
    KcButtonComponent,
    KcIconButtonComponent,
    KcSkeletonComponent,
  ],
  templateUrl: './notifications-bell.component.html',
  styleUrl: './notifications-bell.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class NotificationsBellComponent {
  protected readonly notificationsManager = inject(Kcc20NotificationsManager);
  private readonly tokenStaticProfiles = inject(
    Kcc20TokenStaticProfileCacheService,
  );
  private readonly sanitizer = inject(DomSanitizer);

  protected readonly dropdownOpen = signal(false);
  protected readonly copiedTxid = signal<string | undefined>(undefined);
  protected readonly activeFilter = signal<NotificationFilter>('unread');
  protected readonly skeletonRows = [0, 1, 2];
  protected readonly filters: readonly {
    value: NotificationFilter;
    label: string;
  }[] = [
    { value: 'unread', label: 'Unread' },
    { value: 'all', label: 'All' },
  ];
  protected readonly filteredNotifications = computed(() => {
    const filter = this.activeFilter();
    return this.notificationsManager.notifications().filter((notification) => {
      if (filter === 'all') return true;
      return notification.status !== 'read';
    });
  });

  formatCount = formatCount;
  formatDateMs = formatDateMs;
  shortIdentifier = shortIdentifier;

  constructor() {
    effect(() => {
      const tokenIds = this.notificationsManager
        .notifications()
        .map((notification) => notificationTokenId(notification));
      void this.tokenStaticProfiles.ensureStructural(tokenIds);
    });
  }

  protected toggleDropdown(): void {
    this.dropdownOpen.update((open) => {
      const next = !open;
      if (next) {
        this.activeFilter.set('unread');
      }
      return next;
    });
  }

  protected closeDropdown(): void {
    this.dropdownOpen.set(false);
  }

  protected notificationTitle = notificationTitle;
  protected notificationMessageRows = notificationMessageRows;
  protected notificationCta = notificationCta;
  protected notificationStatusLabel = notificationStatusLabel;
  protected notificationStatusTone = notificationStatusTone;
  protected notificationProminence = notificationProminence;
  protected notificationTransactionHash = notificationTransactionHash;
  protected notificationExplorerHref = notificationExplorerHref;

  protected setFilter(filter: NotificationFilter): void {
    this.activeFilter.set(filter);
  }

  protected safeNotificationHtml(row: string): string {
    return this.sanitizer.sanitize(SecurityContext.HTML, row) ?? '';
  }

  protected notificationTradeSummary(notification: Kcc20Notification) {
    const tokenId = notificationTokenId(notification);
    void this.tokenStaticProfiles.revision();
    const profile = this.tokenStaticProfiles.peek(tokenId);
    return notificationTradeSummary(notification, profile);
  }

  protected markRead(notification: Kcc20Notification): void {
    void this.notificationsManager.markRead(notification.messageId);
  }

  protected markAllRead(): void {
    void this.notificationsManager.markAllRead();
  }

  protected clear(): void {
    void this.notificationsManager.clear();
  }

  protected loadMore(): void {
    void this.notificationsManager.loadMore();
  }

  protected retry(): void {
    void this.notificationsManager.refresh();
  }

  protected onNotificationsScroll(event: Event): void {
    const target = event.target as HTMLElement | null;
    if (!target) return;
    const remaining =
      target.scrollHeight - target.scrollTop - target.clientHeight;
    if (remaining <= 48) {
      this.loadMore();
    }
  }

  protected copyTxid(txid: string): void {
    void navigator.clipboard.writeText(txid).then(() => {
      this.copiedTxid.set(txid);
      window.setTimeout(() => {
        if (this.copiedTxid() === txid) {
          this.copiedTxid.set(undefined);
        }
      }, 1600);
    });
  }
}
